// ShipmentComponents/ShipmentProcessCompact.jsx
import React from "react";
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineDot, TimelineContent } from "@mui/lab";
import { Typography, Box } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useAuth } from "hooks";

const processUrlMap = {
  "Shipment Confirmed": (id) => `/app/crm/shipment/${id}/detail`,
  "Shipment Updated": (id) => `/app/crm/shipment/${id}/detail`,
  "Shipment Collected": (id) => `/app/crm/collection/${id}/detail`,
  "Shipment Consolidated": (id) => `/app/crm/consolidation/${id}/detail`,
  "COC Collection": (id) => `/app/crm/coc/${id}/detail`,
  "Shipment Loaded": (id) => `/app/crm/shipment-loading/${id}/detail`,
  "Shipment Unloaded": (id) => `/app/crm/shipment-unLoading/${id}/detail`,
  "Shipment Deconsolidated": (id) => `/app/crm/deconsolidation/${id}/detail`,
  "Shipment Stored": (id) => `/app/crm/warehousing/${id}/detail`,
  "Shipment Sent Out": (id) => `/app/crm/sendOut/${id}/detail`,
  "Shipment Failed": (id) => `/app/crm/sendOutCollection/${id}/detail`,
  "Shipment Cross Docked": (id) => `/app/crm/cross-docking/${id}/detail`,
  "Shipment Send Out Collected and marked as Delivery Failed": (id) => `/app/crm/sendOutCollection/${id}/detail`,
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = parseISO(dateString);
    return isNaN(date.getTime()) ? "Invalid date" : format(date, "dd MMM, hh:mm a");
  } catch { return "Date error"; }
};

const ShipmentProcessCompact = () => {
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const canEdit = isCustomer || isVendor || isContractor || isDriver;
  const navigate = useNavigate();
  const { ShipmentDetail } = useShipmentDetailContext();
  const processList = ShipmentDetail?.shipmentProcess || [];
  const shipmentId = ShipmentDetail?._id;

  const handleClick = (item) => {
    const { process, relatedId } = item;
    const getUrl = processUrlMap[process];
    const idToUse = ["Shipment Confirmed", "Shipment Updated"].includes(process) ? shipmentId : relatedId;
    if (getUrl && idToUse) navigate(getUrl(idToUse));
    else toast.warning("Detail page not available for this process.");
  };

  const sorted = [...processList].sort((a, b) => {
    const dateA = parseISO(a.createdAt), dateB = parseISO(b.createdAt);
    if (isNaN(dateA.getTime())) return 1;
    if (isNaN(dateB.getTime())) return -1;
    return dateB - dateA;
  });

  return (
    <Box sx={{ p: 1 }}>
      <Timeline position="right" sx={{ p: 0, m: 0, "& .MuiTimelineItem-root::before": { flex: 0, padding: 0 } }}>
        {sorted.map((item, index) => (
          <TimelineItem key={item._id || index} onClick={canEdit ? undefined : () => handleClick(item)} sx={canEdit ? {} : { cursor: "pointer" }}>
            <TimelineSeparator>
              <TimelineDot sx={{ width: 14, height: 14 }} color="primary" />
              {index < sorted.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: "4px", px: 1 }}>
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, textDecoration: "underline" }}>{item.process || "Unknown"}</Typography>
              <Typography sx={{ fontSize: "0.64rem", fontWeight: 600 }}>{formatDate(item.createdAt)} · {item.createdBy || "Unknown"}</Typography>
              {item.description && <Typography sx={{ fontSize: "0.68rem", fontWeight: 600 , mt: 0.25 }}>{item.description}</Typography>}
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Box>
  );
};

export default ShipmentProcessCompact;